/**
 * Column Mapper Component
 *
 * Allows users to map CSV columns to target database fields.
 */

import { Check, X, ArrowRight, Hash, Type, Calculator } from 'lucide-react'
import type { ColumnMappingConfig, CSVColumnInfo, TargetTable } from '../types'

interface ColumnMapperProps {
  columns: CSVColumnInfo[]
  mappings: ColumnMappingConfig[]
  targetTable: TargetTable
  keyColumn: string
  onMappingChange: (index: number, mapping: Partial<ColumnMappingConfig>) => void
  onKeyColumnChange: (column: string) => void
}

// Target field options (only indicator_values). '_indicator' is a UI-only
// sentinel: the actual target is the per-column indicator code (see below).
const INDICATOR_TARGET = '_indicator'

const TARGET_FIELDS: Record<TargetTable, { value: string; label: string }[]> = {
  indicator_values: [
    { value: '_skip', label: 'Nicht importieren' },
    { value: '_key', label: 'Entity-Schlüssel (AGS/ARS)' },
    { value: '_name', label: 'Entity-Name (nur Anzeige)' },
    { value: INDICATOR_TARGET, label: 'Als Indikator importieren' },
  ],
}

const SPECIAL_TARGETS = new Set(['_skip', '_key', '_name'])

/** Derive a stable indicator code from a CSV column name (e.g. "BKI-Regionalfaktor 2025" -> "bki_regionalfaktor_2025"). */
export function slugifyIndicatorCode(columnName: string): string {
  return (
    columnName
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'indikator'
  )
}

// Data type icon
function DataTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'integer':
      return <Hash className="h-4 w-4 text-blue-500" />
    case 'decimal':
      return <Calculator className="h-4 w-4 text-green-500" />
    default:
      return <Type className="h-4 w-4 text-gray-500" />
  }
}

export function ColumnMapper({
  columns,
  mappings,
  targetTable,
  keyColumn,
  onMappingChange,
  onKeyColumnChange,
}: ColumnMapperProps) {
  const targetFields = TARGET_FIELDS[targetTable]

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              CSV-Spalte
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Beispielwerte
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Typ
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
              <ArrowRight className="h-4 w-4 mx-auto" />
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Zielfeld
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Schlüssel
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {mappings.map((mapping, index) => {
            const column = columns.find((c) => c.name === mapping.csv_column)
            const isKeyColumn = mapping.csv_column === keyColumn
            const isSkipped = mapping.skip || mapping.target === '_skip'
            const isIndicatorTarget = !SPECIAL_TARGETS.has(mapping.target)

            return (
              <tr
                key={mapping.csv_column}
                className={`${isSkipped ? 'bg-gray-50 opacity-60' : ''} ${
                  isKeyColumn ? 'bg-blue-50' : ''
                }`}
              >
                {/* CSV Column Name */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="font-medium text-gray-900">
                    {mapping.csv_column}
                  </div>
                </td>

                {/* Sample Values */}
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {column?.sample_values.slice(0, 3).map((val, i) => (
                      <span
                        key={i}
                        className="inline-block px-2 py-0.5 text-xs bg-gray-100 rounded truncate max-w-[120px]"
                        title={val}
                      >
                        {val}
                      </span>
                    ))}
                  </div>
                </td>

                {/* Data Type */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-1">
                    <DataTypeIcon type={mapping.data_type} />
                    <span className="text-sm text-gray-600">
                      {mapping.data_type}
                    </span>
                  </div>
                </td>

                {/* Arrow */}
                <td className="px-4 py-3 text-center">
                  {!isSkipped && (
                    <ArrowRight className="h-4 w-4 mx-auto text-gray-400" />
                  )}
                </td>

                {/* Target Field Selector */}
                <td className="px-4 py-3">
                  <select
                    value={isIndicatorTarget ? INDICATOR_TARGET : mapping.target}
                    onChange={(e) => {
                      const newTarget = e.target.value
                      if (newTarget === INDICATOR_TARGET) {
                        // Each column gets its own indicator code (editable below)
                        onMappingChange(index, {
                          target: slugifyIndicatorCode(mapping.csv_column),
                          skip: false,
                        })
                      } else {
                        onMappingChange(index, {
                          target: newTarget,
                          skip: newTarget === '_skip',
                        })
                      }
                    }}
                    className={`block w-full rounded-md border-gray-300 shadow-sm text-sm
                      focus:border-blue-500 focus:ring-blue-500
                      ${isSkipped ? 'text-gray-400' : ''}
                    `}
                  >
                    {targetFields.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>
                  {isIndicatorTarget && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className="text-xs text-gray-400 whitespace-nowrap">Code:</span>
                      <input
                        type="text"
                        value={mapping.target}
                        onChange={(e) =>
                          onMappingChange(index, { target: e.target.value })
                        }
                        onBlur={(e) => {
                          const cleaned = slugifyIndicatorCode(e.target.value)
                          if (cleaned !== mapping.target) {
                            onMappingChange(index, { target: cleaned })
                          }
                        }}
                        placeholder={slugifyIndicatorCode(mapping.csv_column)}
                        title="Indikator-Code: identifiziert den Indikator dauerhaft. Beim Folgejahres-Import denselben Code verwenden, damit die Werte demselben Indikator zugeordnet werden."
                        className="block w-full rounded-md border-gray-200 shadow-sm text-xs font-mono
                          focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </td>

                {/* Key Column Toggle */}
                <td className="px-4 py-3 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      if (isKeyColumn) {
                        onKeyColumnChange('')
                      } else {
                        onKeyColumnChange(mapping.csv_column)
                        onMappingChange(index, {
                          target: '_key',
                          skip: false,
                        })
                      }
                    }}
                    className={`p-1 rounded-full transition-colors ${
                      isKeyColumn
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                    title={
                      isKeyColumn
                        ? 'Als Schlüsselspalte markiert'
                        : 'Als Schlüsselspalte setzen'
                    }
                  >
                    {isKeyColumn ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Summary */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600 px-4">
        <div>
          <span className="font-medium">{mappings.filter((m) => !m.skip).length}</span> von{' '}
          <span className="font-medium">{mappings.length}</span> Spalten werden importiert
        </div>
        {keyColumn && (
          <div className="flex items-center gap-1">
            <Check className="h-4 w-4 text-green-500" />
            <span>Schlüsselspalte: <strong>{keyColumn}</strong></span>
          </div>
        )}
      </div>
    </div>
  )
}
